import { useState, useRef, useEffect } from 'react';
import { Calendar, X } from 'lucide-react';
import { cn } from '@/utils/cn';
import { Button } from './Button';

export interface DatePreset {
  label: string;
  from: string;
  to: string;
}

export interface DateRangePickerProps {
  from: string | null;
  to: string | null;
  onChange: (from: string | null, to: string | null) => void;
  presets?: DatePreset[];
  includeTime?: boolean;
  className?: string;
  testIdPrefix?: string;
}

const DEFAULT_PRESETS: DatePreset[] = [
  {
    label: 'Last 7 days',
    from: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]!,
    to: new Date().toISOString().split('T')[0]!,
  },
  {
    label: 'Last 30 days',
    from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]!,
    to: new Date().toISOString().split('T')[0]!,
  },
  {
    label: 'Last 90 days',
    from: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]!,
    to: new Date().toISOString().split('T')[0]!,
  },
];

const ALL_TIME_FROM = '2010-01-01';

export function DateRangePicker({
  from,
  to,
  onChange,
  presets = DEFAULT_PRESETS,
  includeTime = false,
  className,
  testIdPrefix,
}: DateRangePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [localFrom, setLocalFrom] = useState(from || '');
  const [localTo, setLocalTo] = useState(to || '');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLocalFrom(from || '');
    setLocalTo(to || '');
  }, [from, to]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleApply = () => {
    onChange(localFrom || null, localTo || null);
    setIsOpen(false);
  };

  const handleClear = () => {
    setLocalFrom('');
    setLocalTo('');
    onChange(null, null);
    setIsOpen(false);
  };

  const handleAllTimePreset = () => {
    const now = new Date();
    const allTimeFrom = includeTime ? `${ALL_TIME_FROM}T00:00` : ALL_TIME_FROM;
    const allTimeTo = includeTime
      ? now.toISOString().slice(0, 16)
      : now.toISOString().split('T')[0]!;

    setLocalFrom(allTimeFrom);
    setLocalTo(allTimeTo);
    onChange(allTimeFrom, allTimeTo);
    setIsOpen(false);
  };

  const handlePresetClick = (preset: DatePreset) => {
    setLocalFrom(preset.from);
    setLocalTo(preset.to);
    onChange(preset.from, preset.to);
    setIsOpen(false);
  };

  const formatDisplayDate = (date: string | null) => {
    if (!date) return '';
    const dateObj = new Date(date);

    if (includeTime) {
      return dateObj.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    }

    return dateObj.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const hasSelection = from || to;
  const displayText = hasSelection
    ? `${formatDisplayDate(from) || 'Start'} - ${formatDisplayDate(to) || 'End'}`
    : 'All time';

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        data-testid={testIdPrefix ? `${testIdPrefix}-toggle` : undefined}
        className={cn(
          'flex items-center gap-2 rounded-lg border border-border bg-white px-3 py-2 text-sm transition-all',
          'hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
          hasSelection && 'border-primary bg-primary-50'
        )}
      >
        <Calendar className="h-4 w-4 text-muted" />
        <span className={cn(hasSelection ? 'text-foreground' : 'text-muted')}>
          {displayText}
        </span>
        {hasSelection && (
          <span
            role="button"
            tabIndex={-1}
            onClick={(e) => {
              e.stopPropagation();
              handleClear();
            }}
            className="ml-1 rounded p-0.5 hover:bg-gray-200"
          >
            <X className="h-3.5 w-3.5 text-muted" />
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute left-0 top-full z-50 mt-2 w-80 rounded-xl border border-border bg-white p-4 shadow-lg">
          <div className="mb-4 space-y-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-muted">From</label>
              <input
                type={includeTime ? 'datetime-local' : 'date'}
                value={localFrom}
                onChange={(e) => setLocalFrom(e.target.value)}
                max={localTo || undefined}
                data-testid={testIdPrefix ? `${testIdPrefix}-from` : undefined}
                className="w-full rounded-lg border border-border px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted">To</label>
              <input
                type={includeTime ? 'datetime-local' : 'date'}
                value={localTo}
                onChange={(e) => setLocalTo(e.target.value)}
                min={localFrom || undefined}
                data-testid={testIdPrefix ? `${testIdPrefix}-to` : undefined}
                className="w-full rounded-lg border border-border px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
          </div>

          <div className="mb-4 border-t border-border pt-4">
            <p className="mb-2 text-xs font-medium text-muted">Quick select</p>
            <div className="flex flex-wrap gap-2">
              {presets.map((preset) => (
                <button
                  key={preset.label}
                  type="button"
                  onClick={() => handlePresetClick(preset)}
                  data-testid={
                    testIdPrefix
                      ? `${testIdPrefix}-preset-${preset.label.toLowerCase().replace(/\s+/g, '-')}`
                      : undefined
                  }
                  className="rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-700 transition-colors hover:bg-gray-200"
                >
                  {preset.label}
                </button>
              ))}
              <button
                type="button"
                onClick={handleAllTimePreset}
                data-testid={testIdPrefix ? `${testIdPrefix}-preset-all-time` : undefined}
                className="rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-700 transition-colors hover:bg-gray-200"
              >
                All time
              </button>
            </div>
          </div>

          <div className="flex justify-end gap-2 border-t border-border pt-4">
            <Button variant="ghost" size="sm" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleApply}
              data-testid={testIdPrefix ? `${testIdPrefix}-apply` : undefined}
            >
              Apply
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
